import React from 'react';
import * as Routes from '@/modules/routes';
import I18n from '@/modules/i18n';
import { TCIDropdown } from '@/components/ui/Dropdown';
import Icon from '@/components/ui/Icon';
import { COLORS } from '@/components/ui/ui.constants';
import cn from '@/utils/cn';
import { GRADE_BAND_LABELS, type GradeBand } from '../constants';

interface CompareHeaderProps {
  availableAIBands: GradeBand[];
  classroomId?: number;
  programId?: number;
  sectionEndPage?: number;
  sectionStartPage?: number;
  sectionTitle?: string;
  selectedGradeBand: GradeBand;
  studentCount: number;
  onClose: () => void;
  onGradeBandChange: (band: GradeBand) => void;
}

const formatPageNumbers = (startPage?: number, endPage?: number): string => {
  if (!startPage) return '';
  if (!endPage || startPage === endPage) return ` (p. ${startPage})`;

  return ` (p. ${startPage} - ${endPage})`;
};

const CompareHeader = ({
  availableAIBands,
  classroomId,
  programId,
  sectionEndPage,
  sectionStartPage,
  sectionTitle,
  selectedGradeBand,
  studentCount,
  onClose,
  onGradeBandChange,
}: CompareHeaderProps) => {
  const pageStr = formatPageNumbers(sectionStartPage, sectionEndPage);
  const title = `${I18n.t('ai_compare_levels_title')} - ${sectionTitle ?? ''}${pageStr}`;
  const isAssigned = studentCount > 0;

  return (
    <div
      className={cn(
        'tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3',
        'tw-bg-light-grey',
      )}
    >
      <span className="tw-text-lg tw-font-bold tw-text-very-dark-grey tw-tracking-[0.2px]">
        {title}
      </span>

      <div className="tw-flex tw-items-center tw-gap-4">
        <div className="tw-flex tw-items-center tw-text-sm">
          <span
            className={cn(
              'tw-inline-block tw-w-3 tw-h-3 tw-rounded-full',
              isAssigned ? 'tw-bg-green' : 'tw-bg-med-grey',
            )}
          />
          <span className="tw-font-bold tw-text-tci-black tw-ml-2">
            {isAssigned
              ? I18n.t('ai_level_available_to_students', { count: studentCount })
              : I18n.t('ai_level_not_assigned')}
          </span>
          {classroomId && (
            <a
              aria-label="Edit student assignments"
              className="tw-text-tci-purple tw-flex tw-items-center tw-ml-2.5"
              href={Routes.teacher_classroom_students_path(classroomId, { programId })}
              rel="noopener noreferrer"
              target="_blank"
            >
              <Icon color={COLORS['tci-purple']} icon="EditPreview" size={18} />
            </a>
          )}
        </div>

        {availableAIBands.length > 0 && (
          <TCIDropdown>
            <TCIDropdown.Trigger asChild>
              <button
                className={cn(
                  'tw-flex tw-items-center tw-h-9 tw-min-w-[158px] tw-px-2.5',
                  'tw-bg-white tw-border tw-border-solid tw-border-med-grey',
                  'tw-rounded tw-cursor-pointer hover:tw-shadow-md tw-text-base tw-font-tci',
                )}
                type="button"
              >
                <span className="tw-flex tw-items-center tw-gap-1.5">
                  <Icon color={COLORS['tci-purple']} icon="ReadingLevel" size={22} />
                  <span className="tw-text-tci-black">{GRADE_BAND_LABELS[selectedGradeBand]}</span>
                </span>
                <Icon className="tw-ml-auto" icon="ChevronDown" size={12} />
              </button>
            </TCIDropdown.Trigger>
            <TCIDropdown.Content align="end" side="bottom" width="default">
              {availableAIBands.map(band => (
                <TCIDropdown.Item
                  key={band}
                  aria-current={band === selectedGradeBand ? 'true' : undefined}
                  onSelect={() => onGradeBandChange(band)}
                >
                  <span className="tw-flex tw-items-center tw-gap-2">
                    {band === selectedGradeBand ? (
                      <Icon aria-hidden="true" color={COLORS.green} icon="Checkmark" size={16} />
                    ) : (
                      <span className="tw-w-4" />
                    )}
                    <span>{GRADE_BAND_LABELS[band]}</span>
                  </span>
                </TCIDropdown.Item>
              ))}
            </TCIDropdown.Content>
          </TCIDropdown>
        )}

        <button
          aria-label={I18n.t('close')}
          className="tw-cursor-pointer tw-bg-transparent tw-border-0 tw-p-1 tw-text-very-dark-grey hover:tw-opacity-70"
          type="button"
          onClick={onClose}
        >
          <Icon icon="Close" size={20} />
        </button>
      </div>
    </div>
  );
};

export { formatPageNumbers };
export default CompareHeader;
